import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { injectIntl, FormattedMessage } from 'react-intl'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import Input from '@vtex/styleguide/lib/Input'
import Button from '@vtex/styleguide/lib/Button'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import { checkoutInstoreCustomJs } from '@/checkout-instore-legacy/constants/CustomScriptConstants'
import { getAccountName } from '@/checkout-instore-legacy/utils/UrlUtils'
import { sendEvent } from '@/checkout-instore-legacy/utils/WebViewBridge'
import * as EmailUtils from '@/checkout-instore-legacy/utils/EmailUtils'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import { logClickEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'
import Icon from '@/checkout-instore-legacy/components/Commons/Icons'
import Pattern from '@/checkout-instore-legacy/assets/images/vtex_pattern.svg'

import OAuthButton from './OAuthButton'
import SamlButtons from './SamlButtons'

class LoginForm extends Component {
  constructor(props) {
    super(props)

    this.state = {
      email: '',
      password: '',
      isEmailValid: false,
      invalidUser: false,
      isPasswordValid: false,
      canShowPasswordWarning: false,
      canShowEmailWarning: false,
      isWrongCredentials: false,
      showLoginAndPassword: get(
        getInstoreConfig(),
        ['auth', 'showLoginAndPassword'],
        true
      ),
      showSaml: get(getInstoreConfig(), ['auth', 'showSaml'], true),
      oAuthProviderName: get(getInstoreConfig(), ['auth', 'oAuthProviderName']),
    }
  }

  componentDidMount() {
    CustomEvent.listenEvent('vendor.autologin', this.handleAutoLogin)
    this.props.refreshLoginProviders()
    CustomEvent.listenEvent('load.instore-custom', (event) => {
      if (event && event.data && event.data.src === checkoutInstoreCustomJs) {
        this.setState({
          showSaml: get(getInstoreConfig(), ['auth', 'showSaml'], true),
          oAuthProviderName: get(getInstoreConfig(), [
            'auth',
            'oAuthProviderName',
          ]),
        })
      }
    })
  }

  componentWillUnmount() {
    CustomEvent.unlistenEvent('vendor.autologin', this.handleAutoLogin)
  }

  componentDidUpdate = (prevProps) => {
    const { error } = this.props
    if (error !== prevProps.error) {
      if (error && error.isInline) {
        this.setState({ isWrongCredentials: true })
      }
    }
  }

  handleAutoLogin = (event) => {
    if (!event || !event.data || !event.data.email) {
      return
    }
    if (event.data.shouldAutoSubmit) {
      this.props.login(event.data.email, event.data.password)
    } else {
      const isEmailValid = EmailUtils.validateEmail(event.data.email)
      const isPasswordValid = !!(event.data.password || this.state.password)
      this.setState({
        email: event.data.email,
        password: event.data.password || this.state.password,
        isEmailValid,
        canShowEmailWarning: !isEmailValid,
        isPasswordValid,
        canShowPasswordWarning: !isPasswordValid,
      })
    }
  }

  handleEmailChange = (e) => {
    const isEmailValid = EmailUtils.validateEmail(e.target.value)
    if (this.state.isWrongCredentials) {
      this.props.clearError()
    }
    this.setState({
      email: e.target.value,
      isEmailValid,
      isWrongCredentials: false,
      canShowEmailWarning: !isEmailValid,
    })
  }

  handlePasswordChange = (e) => {
    if (this.state.isWrongCredentials) {
      this.props.clearError()
    }
    this.setState({
      password: e.target.value,
      isPasswordValid: !(e.target.value === '' || null),
      isWrongCredentials: false,
    })
    if (!(e.target.value === '' || null)) {
      this.setState({
        canShowPasswordWarning: false,
      })
    }
  }

  handleSubmit = async (e) => {
    e.preventDefault()
    this.props.clearError()
    this.setState({
      canShowPasswordWarning: !this.state.isPasswordValid,
      canShowEmailWarning: !this.state.isEmailValid,
      isWrongCredentials: false,
      loading: true,
    })

    logClickEvent('click-login-button', 'Admin')

    if (this.state.isEmailValid && this.state.isPasswordValid) {
      this.props.login(this.state.email, this.state.password)
    }

    this.setState({
      loading: false,
    })
  }

  handleSamlSubmit = (samlId) => {
    logClickEvent('click-saml-login-button', 'Admin', { samlId })

    if (samlId) {
      this.props.loginWithSaml(samlId)
    }
  }

  handleOAuthSubmit = (oAuthProviderName) => {
    logClickEvent('click-oauth-login-button', 'Admin', { oAuthProviderName })

    if (oAuthProviderName) {
      this.props.loginWithOAuth(oAuthProviderName)
    }
  }

  handleEmailBlur = () => {
    this.setState({ canShowEmailWarning: !this.state.isEmailValid })
  }

  handlePasswordBlur = () => {
    this.setState({ canShowPasswordWarning: !this.state.isPasswordValid })
  }

  handleChangeAccount = () => {
    sendEvent({
      type: 'changeAccountName.vtex',
    })
  }

  render() {
    const accountName = getAccountName()
    const { intl } = this.props
    const { oAuthProviderName } = this.state
    const samlProviders = get(this.props, 'samlProviders', [])
    const oAuthProviders = get(this.props, 'oAuthProviders', [])
    const samlExists = this.state.showSaml && !isEmpty(samlProviders)
    const oAuthExists =
      !!oAuthProviderName &&
      !!oAuthProviders.find(
        (oAuthProvider) => oAuthProvider.providerName === oAuthProviderName
      )
    const showLoginAndPassword =
      this.state.showLoginAndPassword || (!samlExists && !oAuthExists)

    const messageWarningClass = this.state.canShowEmailWarning
      ? 'b--light-red'
      : 'b--black-20'

    return (
      <div
        className="h-100 bg-light-silver flex items-center justify-center min-height"
        style={{
          backgroundImage: `url(${Pattern})`,
          backgroundPosition: '50% 50%',
          backgroundSize: 'cover',
        }}
      >
        <div className="flex flex-column justify-between h-auto-ns w-70-ns mw6 bg-base ph7 ph9-ns pt6-ns pb7 shadow-6">
          <div className="pb3-m">
            <Icon type="nc-VTEX-logo" color="#F71963" width={120} />
          </div>
          <div>
            <div className="f3 c-on-base lh-copy-ns mb7">
              <FormattedMessage id="loginToStartSelling" />
              <span className="f3 vtex-pink">
                <FormattedMessage id="salesAppProductName" />
              </span>
            </div>
            {samlExists ? (
              <SamlButtons
                samlProviders={samlProviders}
                className="pb2"
                buttonClassName={showLoginAndPassword ? 'mb5 pb2' : ''}
                loading={this.state.loading}
                includeOrLabel={showLoginAndPassword}
                onSamlSubmit={this.handleSamlSubmit}
              />
            ) : oAuthExists ? (
              <div className="f6 truncate pb2">
                <OAuthButton
                  idpName={oAuthProviderName}
                  className={showLoginAndPassword ? 'mb5 pb2' : ''}
                  loading={this.state.loading}
                  includeOrLabel={showLoginAndPassword}
                  onClick={() => this.handleOAuthSubmit(oAuthProviderName)}
                />
              </div>
            ) : null}

            {showLoginAndPassword && (
              <form className="mt5 truncate" onSubmit={this.handleSubmit}>
                <div className="mb5">
                  <Input
                    id="loginform-email"
                    className={`ba br2 mb4 w-100 pa5 ${
                      this.state.isEmailValid
                        ? 'b--black-20'
                        : messageWarningClass
                    }`}
                    type="email"
                    size="large"
                    placeholder="Email"
                    value={this.state.email}
                    onChange={this.handleEmailChange}
                    onBlur={this.handleEmailBlur}
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    errorMessage={
                      this.state.canShowEmailWarning && !this.state.isEmailValid
                        ? intl.formatMessage({ id: 'enterValidEmail' })
                        : null
                    }
                  />
                </div>
                <div className="mt4">
                  <Input
                    id="loginform-password"
                    className={`ba br2 mb4 w-100 pa5 ${
                      this.state.isPasswordValid
                        ? 'b--black-20'
                        : messageWarningClass
                    }`}
                    type="password"
                    size="large"
                    placeholder={intl.formatMessage({
                      id: 'passwordInputPlaceholder',
                    })}
                    value={this.state.password}
                    onChange={this.handlePasswordChange}
                    onBlur={this.handlePasswordBlur}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    errorMessage={
                      this.state.canShowPasswordWarning &&
                      !this.state.isPasswordValid
                        ? intl.formatMessage({ id: 'enterYourPassword' })
                        : null
                    }
                  />
                  {this.state.isWrongCredentials && (
                    <div className="red f6 mt3 lh-title">
                      {intl.formatMessage({ id: 'wrongUser' })}
                    </div>
                  )}
                </div>
                <div className="w-100 mt5 center">
                  <Button
                    isLoading={this.state.loading}
                    disabled={this.state.loading}
                    id="loginform-submit"
                    size="regular"
                    type="submit"
                    variation={
                      samlExists || oAuthExists ? 'secondary' : 'primary'
                    }
                    block
                    inline={false}
                  >
                    <FormattedMessage id="loginButtonLabel" />
                  </Button>
                </div>
              </form>
            )}
            <div className="f6 c-muted-2 tc lh-copy mt5">
              <FormattedMessage id="requestAccessToManagement" />
            </div>
          </div>
          <div className="f6 mt6">
            <div className="mid-gray">
              <FormattedMessage id="accountLabel" />
            </div>
            <div className="flex justify-between items-baseline">
              <div className="truncate">{accountName}</div>
              <div className="nr5">
                <Button
                  size="small"
                  variation="tertiary"
                  className="pv4 pl5 bn bg-transparent c-action-primary pointer"
                  onClick={this.handleChangeAccount}
                >
                  <FormattedMessage id="changeAccount" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

LoginForm.propTypes = {
  intl: intlShape.isRequired,
  error: PropTypes.object,
  login: PropTypes.func.isRequired,
  loginWithSaml: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  samlProviders: PropTypes.array,
  oAuthProviders: PropTypes.array,
  refreshLoginProviders: PropTypes.func.isRequired,
}

export default injectIntl(LoginForm)
